import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Layout } from 'react-grid-layout';

export interface WidgetData {
  id: string; // Unique instance ID
  type: string; // Maps to a component in WidgetRegistry
  title?: string; // Optional custom title
}

export interface WidgetLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardTab {
  id: string;
  title: string;
  layout: WidgetLayoutItem[];
  widgets: WidgetData[];
}

export interface DashboardState {
  tabs: DashboardTab[];
  activeTabId: string;
  setActiveTab: (id: string) => void;
  addTab: (title: string) => void;
  removeTab: (id: string) => void;
  updateLayout: (tabId: string, layout: WidgetLayoutItem[]) => void;
  addWidget: (tabId: string, type: string, w?: number, h?: number) => void;
  removeWidget: (tabId: string, widgetId: string) => void;
  resetDashboard: () => void;
  /** Restore one built-in tab to its default widgets and layout. */
  resetTab: (tabId: string) => void;
}

/**
 * Closes horizontal holes: the grid only compacts upwards, so removing a card
 * used to leave an empty slot in its row. Items that share a row (same y) are
 * slid left in their current order; widths, heights and rows are unchanged.
 */
export function packRows(layout: WidgetLayoutItem[]): WidgetLayoutItem[] {
  const rows = new Map<number, WidgetLayoutItem[]>();
  for (const item of layout) rows.set(item.y, [...(rows.get(item.y) || []), item]);
  const packed: WidgetLayoutItem[] = [];
  for (const items of rows.values()) {
    let x = 0;
    for (const item of [...items].sort((a, b) => a.x - b.x)) {
      packed.push({ ...item, x: Math.min(x, Math.max(0, 12 - item.w)) });
      x += item.w;
    }
  }
  return packed;
}

const HR_TAB: DashboardTab = {
  id: 'default-hr',
  title: 'HR Dashboard',
  layout: [
    { i: 'hr-attendance', x: 0, y: 0, w: 5, h: 5 },
    { i: 'hr-leave', x: 5, y: 0, w: 4, h: 5 },
    { i: 'hr-payroll', x: 9, y: 0, w: 3, h: 5 },
    { i: 'hr-trend', x: 0, y: 5, w: 12, h: 5 },
  ],
  widgets: [
    { id: 'hr-attendance', type: 'hr-attendance' },
    { id: 'hr-leave', type: 'hr-leave' },
    { id: 'hr-payroll', type: 'hr-payroll' },
    { id: 'hr-trend', type: 'chart-attendance' },
  ],
};

const SELF_SERVICE_TAB: DashboardTab = {
  id: 'default-self-service',
  title: 'Employee Self-Service',
  layout: [
    { i: 'self-service-overview', x: 0, y: 0, w: 12, h: 3 },
    { i: 'self-service-requests', x: 0, y: 3, w: 6, h: 5 },
    { i: 'self-service-catalog', x: 6, y: 3, w: 6, h: 5 },
    { i: 'self-service-assets', x: 0, y: 8, w: 6, h: 4 },
    { i: 'self-service-leave', x: 6, y: 8, w: 6, h: 4 },
  ],
  widgets: [
    { id: 'self-service-overview', type: 'enduser-self-service' },
    { id: 'self-service-requests', type: 'enduser-my-requests' },
    { id: 'self-service-catalog', type: 'enduser-service-catalog' },
    { id: 'self-service-assets', type: 'enduser-my-assets' },
    { id: 'self-service-leave', type: 'hr-leave' },
  ],
};

const DEFAULT_TABS: DashboardTab[] = [
  {
    id: 'default-main',
    title: 'My Dashboard',
    layout: [
      { i: 'overview-people', x: 0, y: 0, w: 3, h: 5 },
      { i: 'hr-attendance', x: 3, y: 0, w: 3, h: 5 },
      { i: 'overview-support', x: 6, y: 0, w: 3, h: 5 },
      { i: 'overview-assets', x: 9, y: 0, w: 3, h: 5 },
      { i: 'chart-attendance', x: 0, y: 5, w: 8, h: 5 },
      { i: 'recent-activity', x: 8, y: 5, w: 4, h: 5 },
      { i: 'quick-hubs', x: 0, y: 10, w: 12, h: 2 },
    ],
    widgets: [
      { id: 'overview-people', type: 'overview-people' },
      { id: 'hr-attendance', type: 'hr-attendance' },
      { id: 'overview-support', type: 'overview-support' },
      { id: 'overview-assets', type: 'overview-assets' },
      { id: 'chart-attendance', type: 'chart-attendance' },
      { id: 'recent-activity', type: 'recent-activity' },
      { id: 'quick-hubs', type: 'quick-hubs' },
    ]
  },
  SELF_SERVICE_TAB,
  HR_TAB,
];

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set) => ({
      tabs: DEFAULT_TABS,
      activeTabId: 'default-main',
      
      setActiveTab: (id) => set({ activeTabId: id }),
      
      addTab: (title) => set((state) => {
        const newTab: DashboardTab = {
          id: `tab-${Date.now()}`,
          title,
          layout: [],
          widgets: []
        };
        return { tabs: [...state.tabs, newTab], activeTabId: newTab.id };
      }),
      
      removeTab: (id) => set((state) => ({
        tabs: state.tabs.filter(t => t.id !== id),
        activeTabId: state.activeTabId === id ? state.tabs[0]?.id || '' : state.activeTabId
      })),
      
      updateLayout: (tabId, layout) => set((state) => ({
        tabs: state.tabs.map(tab => 
          tab.id === tabId ? { ...tab, layout } : tab
        )
      })),
      
      addWidget: (tabId, type, w = 3, h = 3) => set((state) => {
        const widgetId = `${type}-${Date.now()}`;
        return {
          tabs: state.tabs.map(tab => {
            if (tab.id !== tabId) return tab;

            // Find an empty spot or just put at the bottom
            const newY = tab.layout.length > 0 ? Math.max(...tab.layout.map(l => l.y + l.h)) : 0;
            const newLayoutItem: WidgetLayoutItem = {
              i: widgetId,
              x: 0,
              y: newY,
              w,
              h
            };
            
            return {
              ...tab,
              widgets: [...tab.widgets, { id: widgetId, type }],
              layout: [...tab.layout, newLayoutItem]
            };
          })
        };
      }),
      
      removeWidget: (tabId, widgetId) => set((state) => ({
        tabs: state.tabs.map(tab => {
          if (tab.id !== tabId) return tab;
          return {
            ...tab,
            widgets: tab.widgets.filter(w => w.id !== widgetId),
            layout: packRows(tab.layout.filter(l => l.i !== widgetId))
          };
        })
      })),

      resetDashboard: () => set({ tabs: DEFAULT_TABS, activeTabId: 'default-main' }),

      resetTab: (tabId) => set((state) => {
        const fallback = DEFAULT_TABS.find((t) => t.id === tabId);
        return {
          tabs: state.tabs.map((tab) => (tab.id !== tabId ? tab
            : fallback ? { ...fallback, title: tab.title } : { ...tab, widgets: [], layout: [] })),
        };
      })
    }),
    {
      name: 'teamone-dashboard-storage',
      // Bump when DEFAULT_TABS layout changes so existing (clipped) saved
      // layouts reset to the improved sizing instead of loading stale heights.
      // v3: earlier builds used a Responsive grid that recompacted the single
      // stored layout to a narrow breakpoint and saved it back, collapsing every
      // widget into one column (x:0, w:2). Reset those corrupted layouts.
      // v4: widgets show live data. The main tab's fake finance chart becomes the
      // attendance trend (same place and size), the overview cards get one more
      // row for their numbers, and an empty HR tab gets its default widgets.
      // Anything else the user arranged is kept.
      // v6: close the holes earlier removals left in saved layouts (packRows) and
      // give summary cards one more row for the taller card header.
      version: 6,
      migrate: (persisted: any, version) => {
        if (version < 3) {
          return { ...persisted, tabs: DEFAULT_TABS, activeTabId: 'default-main' };
        }
        if (version < 4) {
          const tabs = (persisted?.tabs || []).map((tab: DashboardTab) => {
            if (tab.id === 'default-main') {
              const overview = new Set(['overview-people', 'overview-assets', 'overview-support', 'overview-finance']);
              return {
                ...tab,
                widgets: tab.widgets.map((w) => (w.id === 'chart-finance' && w.type === 'chart-finance' ? { id: 'chart-attendance', type: 'chart-attendance' } : w)),
                layout: tab.layout.map((l) => (l.i === 'chart-finance' ? { ...l, i: 'chart-attendance' }
                  : overview.has(l.i) && l.h < 4 ? { ...l, h: 4 } : l)),
              };
            }
            if (tab.id === 'default-hr' && tab.widgets.length === 0) return HR_TAB;
            return tab;
          });
          persisted = { ...persisted, tabs };
        }
        if (version < 6) {
          // Also give the summary cards one more row: the card header grew to 44px in v5.
          const summary = new Set(['overview-people', 'overview-assets', 'overview-support', 'overview-finance', 'hr-attendance']);
          return {
            ...persisted,
            tabs: (persisted?.tabs || []).map((tab: DashboardTab) => {
              const types = new Map(tab.widgets.map((w) => [w.id, w.type]));
              return { ...tab, layout: packRows((tab.layout || []).map((l) => (summary.has(types.get(l.i) || '') && l.h < 5 ? { ...l, h: 5 } : l))) };
            }),
          };
        }
        return persisted;
      },
    }
  )
);
